import React, { useState, useEffect, useRef } from "react";
import { SectionCard, SectionTitle, InputField } from "../components/shared";
import { VARIABLES, Template } from "../constants";
import styles from "../../../../styles/mailCenter/composeTab.module.scss";
import RichTextEditor from "../components/RichTextEditor";
import axios from "axios";
import swal from "sweetalert";
import { isAuth } from "../../../../pages/auth/ProtectedRoute";
import { useQuery } from "@tanstack/react-query";
// import logo from "../../../../../src/assets/logo.png";


const AUDIENCE_OPTIONS: AudienceOption[] = [
  { label: "All Users", value: "all" },
  { label: "Instructors", value: "instructors" },
  { label: "All Students", value: "students" },
  { label: "All Mentors", value: "mentors" },
  { label: "All Mentees", value: "mentees" },
  { label: "Specific Users", value: "specific" }
];

const SEND_OPTIONS: SendOption[] = [
  "Send now",
  "Schedule for later",
  "Save as draft",
];

const StarIcon: React.FC<StarIconProps> = ({ stroke = "#4895EF" }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.95829 9.66423C5.89877 9.43351 5.77851 9.22296 5.61003 9.05448C5.44155 8.886 5.231 8.76575 5.00029 8.70623L0.910286 7.65156C0.840506 7.63176 0.779092 7.58973 0.73536 7.53186C0.691629 7.47399 0.667969 7.40343 0.667969 7.3309C0.667969 7.25836 0.691629 7.1878 0.73536 7.12993C0.779092 7.07206 0.840506 7.03003 0.910286 7.01023L5.00029 5.9549C5.23092 5.89543 5.44141 5.77528 5.60989 5.60692C5.77836 5.43856 5.89866 5.22815 5.95829 4.99756L7.01295 0.907562C7.03256 0.837508 7.07454 0.77579 7.1325 0.731825C7.19046 0.68786 7.26121 0.664062 7.33395 0.664062C7.4067 0.664062 7.47745 0.68786 7.53541 0.731825C7.59336 0.77579 7.63535 0.837508 7.65495 0.907562L8.70895 4.99756C8.76847 5.22828 8.88873 5.43883 9.05721 5.60731C9.22569 5.77579 9.43624 5.89604 9.66695 5.95556L13.757 7.00956C13.8273 7.02896 13.8893 7.0709 13.9335 7.12895C13.9777 7.18699 14.0017 7.25794 14.0017 7.3309C14.0017 7.40385 13.9777 7.4748 13.9335 7.53284C13.8893 7.59089 13.8273 7.63283 13.757 7.65223L9.66695 8.70623C9.43624 8.76575 9.22569 8.886 9.05721 9.05448C8.88873 9.22296 8.76847 9.43351 8.70895 9.66423L7.65429 13.7542C7.63468 13.8243 7.5927 13.886 7.53474 13.93C7.47678 13.9739 7.40603 13.9977 7.33329 13.9977C7.26054 13.9977 7.18979 13.9739 7.13183 13.93C7.07388 13.886 7.03189 13.8243 7.01229 13.7542L5.95829 9.66423Z"
      stroke={stroke}
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UsersIcon: React.FC<UsersIconProps> = ({
  size = 16,
  color = "currentColor",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.8"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ChevronDownIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplateId,
  onSelectTemplate,
}) => (
  <SectionCard className={styles["template-selector"]}>
    <SectionTitle>Email Template</SectionTitle>
    <div className={styles["template-selector__wrapper"]}>
      <select
        className={styles["template-selector__select"]}
        value={selectedTemplateId ?? ""}
        onChange={(e) => onSelectTemplate(e.target.value)}
      >
        <option value="">Select a template</option>
        {templates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
          </option>
        ))}
      </select>
      <span className={styles["template-selector__chevron"]}>▾</span>
    </div>
  </SectionCard>
);

const EmailDetailsForm: React.FC<EmailDetailsFormProps> = ({
  fromName,
  fromEmail,
  subject,
  body,
  richBody,
  bodyMode,
  onFromNameChange,
  onFromEmailChange,
  onSubjectChange,
  onBodyChange,
  onRichBodyChange,
  onBodyModeChange,
  onInsertVariable,
  editorRef,
}) => {
  const date = new Date();
  const year = date.getFullYear();

  return (
    <SectionCard>
      <SectionTitle>Email Details</SectionTitle>
      <div className={styles["email-form__fields"]}>
        <div className={styles["email-form__row"]}>
          <InputField
            label="From Name"
            placeholder="Kominiti Team"
            value={fromName}
            onChange={(e) => onFromNameChange(e.target.value)}
          />
          <InputField
            label="From Email"
            placeholder="noreply@kominiti.com"
            value={fromEmail}
            onChange={(e) => onFromEmailChange(e.target.value)}
          />
        </div>

        <InputField
          label="Subject Line"
          required
          placeholder="e.g., Welcome to Kominiti, {{first_name}}!"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
        />

        <div className={styles["email-form__variables"]}>
          <div className={styles["email-form__variables-header"]}>
            <StarIcon stroke="#4895EF" />
            <span className={styles["email-form__variables-label"]}>
              Suggested Variables
            </span>
          </div>
          <div className={styles["email-form__variables-chips"]}>
            {VARIABLES.map((v) => (
              <span
                key={v}
                className={styles["email-form__variables-chip"]}
                onClick={() => onInsertVariable(v)}
              >
                {v}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className={styles["email-form__body-header"]}>
            <label className={styles["email-form__body-label"]}>
              Email Body{" "}
              <span className={styles["email-form__body-label-required"]}>
                *
              </span>
            </label>
          </div>
          <RichTextEditor
            ref={editorRef}
            bodyMode={bodyMode}
            onBodyModeChange={onBodyModeChange}
            plainTextValue={body}
            onPlainTextChange={onBodyChange}
            richTextValue={richBody}
            onRichTextChange={onRichBodyChange}
            placeholder="Write your email content here. Use variables like {{first_name}} for personalization."
            // htmlTemplate={`<div style="font-family: Arial, sans-serif; line-height: 1.5;">
            //   <html> <head> <meta charset="UTF-8" /> <title>Welcome Email</title> <style> body { margin: 0; padding: 0; background-color: #f4f6f9; font-family: Arial, Helvetica, sans-serif; color: #333333; } .container { max-width: 600px; margin: 40px auto; background: #ffffff; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.08); } .header { background: #2563eb; text-align: center; padding: 40px 30px; } .logo { width: 150px; margin-bottom: 20px; } .header h1 { color: #ffffff; margin: 0; font-size: 28px; } .content { padding: 40px; line-height: 1.8; } .content h2 { color: #1e293b; margin-top: 0; } .button-wrapper { text-align: center; margin: 35px 0; } .button { display: inline-block; background: #2563eb; color: #ffffff !important; text-decoration: none; padding: 14px 30px; border-radius: 6px; font-size: 16px; font-weight: 600; } .footer { background: #f8fafc; padding: 30px; text-align: center; color: #777777; font-size: 13px; border-top: 1px solid #e5e7eb; } .footer a { color: #2563eb; text-decoration: none; } @media screen and (max-width: 600px) { .content { padding: 25px; } .header { padding: 30px 20px; } } </style> </head> <body> <div class="container"> <div class="header"> <img src="https://pngfre.com/wp-content/uploads/spiderman-poster.png" class="logo" alt="Logo"> <h1>Welcome to {{company_name}}</h1> </div> <div class="content"> 
              
            //  {{content}}
              
            //   <div class="footer"> <p> © 2026 kominiti. All rights reserved. </p> <p> <a href="https://www.kominiti.com/">Visit Website</a> </p> </div> </div> </body> </html>
            
            // </div>`}
          />
        </div>
      </div>
    </SectionCard>
  );
};

const RecipientsBadge: React.FC<RecipientsBadgeProps> = ({ count }) => (
  <span className={styles["recipients-panel__badge"]}>{count} recipients</span>
);


const AudienceDropdown: React.FC<AudienceDropdownProps> = ({
  selectedAudience,
  audienceConditions,
  selectedUsers,
  onSelectAudience,
  onAddCondition,
  onUpdateCondition,
  onRemoveCondition,
  onSelectUser,
  onRemoveUser,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<Recipient[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const handleSearchUsers = async (query: string) => {
    setSearchQuery(query);
    const trimmed = query.trim();

    // only search when user has typed at least 3 characters
    if (trimmed.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_HOST_API}mail-center/members?search=${encodeURIComponent(query)}`,
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
          },
        }
      );
      const members = response.data?.data?.data || [];
      setSearchResults(members);
      setShowResults(true);
    } catch (error) {
      console.error("Error searching users:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleUserSelect = (user: Recipient) => {
    onSelectUser(user);
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
  };

  const isUserSelected = (userId: number) => 
    selectedUsers.some(u => u.id === userId);

  return (
    <div className={styles["audience_dropdown"]}>
      <div className={styles["recipients-panel__dropdown-row"]}>
        <select
          className={styles["recipients-panel__dropdown-select"]}
          value={selectedAudience}
          onChange={(e) => onSelectAudience(e.target.value)}
        >
          {AUDIENCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {selectedAudience === "specific" ? (
        <div className={styles["recipients-panel__search-container"]}>
          <div className={styles["recipients-panel__search-row"]}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchUsers(e.target.value)}
              placeholder="Search users..."
              className={styles["recipients-panel__search-input"]}
            />
          </div>

          {showResults && searchResults.length > 0 && (
            <div className={styles["recipients-panel__search-results"]}>
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  className={`${styles["recipients-panel__search-result-item"]} ${
                    isUserSelected(user.id)
                      ? styles["recipients-panel__search-result-item--selected"]
                      : ""
                  }`}
                  onClick={() => handleUserSelect(user)}
                >
                  <div className={styles["recipients-panel__search-result-content"]}>
                    <div className={styles["recipients-panel__search-result-name"]}>
                      {user.fullname}
                    </div>
                    <div className={styles["recipients-panel__search-result-email"]}>
                      {user.email}
                    </div>
                  </div>
                  {isUserSelected(user.id) && (
                    <div className={styles["recipients-panel__search-result-check"]}>✓</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {selectedUsers.length > 0 && (
            <div className={styles["recipients-panel__selected-users"]}>
              <div className={styles["recipients-panel__selected-users-label"]}>Selected Users ({selectedUsers.length})</div>
              <div className={styles["recipients-panel__selected-users-list"]}>
                {selectedUsers.map((user) => (
                  <div key={user.id} className={styles["recipients-panel__user-chip"]}>
                    <span>{user.fullname}</span>
                    <button
                      type="button"
                      className={styles["recipients-panel__user-chip-remove"]}
                      onClick={() => onRemoveUser(user.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {selectedAudience !== "all" && (
            <div className={styles["recipients-panel__conditions"]}>
              <button
                type="button"
                className={styles["recipients-panel__condition-btn"]}
                onClick={onAddCondition}
              >
                + Add AND condition
              </button>
            </div>
          )}

          {audienceConditions.map((condition) => (
            <div key={condition.id} className={styles["recipients-panel__condition-block"]}>
              <span className={styles["recipients-panel__condition-logic"]}>AND</span>
              <select
                className={styles["recipients-panel__dropdown-select"]}
                value={condition.target_type}
                onChange={(e) => onUpdateCondition(condition.id, e.target.value)}
              >
                {AUDIENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={styles["recipients-panel__condition-btn"]}
                onClick={() => onRemoveCondition(condition.id)}
              >
                Remove
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const WhoReceivesBox: React.FC<WhoReceivesBoxProps> = ({
  audience = "All Users",
}) => (
  <div className={styles["recipients-panel__who-box"]}>
    <div className={styles["recipients-panel__who-box-header"]}>
      <span className={styles["recipients-panel__who-box-icon"]}>
        <InfoIcon />
      </span>
      <span className={styles["recipients-panel__who-box-heading"]}>
        Who Will Receive This
      </span>
    </div>
    <p className={styles["recipients-panel__who-box-text"]}>
      This email will be sent to users who match:
    </p>
    <span className={styles["recipients-panel__who-box-tag"]}>
      <UsersIcon size={12} color="#6B7280" />
      {audience}
    </span>
  </div>
);

const SendTestEmailBox: React.FC = () => {
  const [testEmail, setTestEmail] = useState<string>("");

  return (
    <div className={styles["recipients-panel__test-box"]}>
      <p className={styles["recipients-panel__test-box-title"]}>
        Send Test Email
      </p>
      <input
        type="email"
        placeholder="your.email@example.com"
        value={testEmail}
        onChange={(e) => setTestEmail(e.target.value)}
        className={styles["recipients-panel__test-box-input"]}
      />
      <button className={styles["recipients-panel__test-box-btn"]}>
        Send Test
      </button>
    </div>
  );
};


const TargetRecipientsPanel: React.FC<TargetRecipientsPanelProps> = ({
  selectedAudience,
  audienceConditions,
  selectedUsers,
  onSelectAudience,
  onAddCondition,
  onUpdateCondition,
  onRemoveCondition,
  onSelectUser,
  onRemoveUser,
}) => {
  const selectedAudienceLabel =
    AUDIENCE_OPTIONS.find((option) => option.value === selectedAudience)
      ?.label || "All Users";
  const conditionLabels = audienceConditions.map(
    (condition) =>
      AUDIENCE_OPTIONS.find((option) => option.value === condition.target_type)
        ?.label || condition.target_type,
  );
  const audienceLabel =
    selectedAudience === "all" && conditionLabels.length === 0
      ? "All Users"
      : [selectedAudienceLabel, ...conditionLabels].join(" AND ");

  return (
    <div className={styles["recipients-panel"]}>
      <SectionCard className={styles["recipients-panel__card"]}>
        <div className={styles["recipients-panel__header"]}>
          <span className={styles["recipients-panel__title"]}>
            Target Recipients
          </span>
          {/* <RecipientsBadge count={42} /> */}
        </div>

        <AudienceDropdown
          selectedAudience={selectedAudience}
          audienceConditions={audienceConditions}
          selectedUsers={selectedUsers}
          onSelectAudience={onSelectAudience}
          onAddCondition={onAddCondition}
          onUpdateCondition={onUpdateCondition}
          onRemoveCondition={onRemoveCondition}
          onSelectUser={onSelectUser}
          onRemoveUser={onRemoveUser}
        />
        <WhoReceivesBox audience={audienceLabel} />
      </SectionCard>
      {/* <SendTestEmailBox /> */}
    </div>
  );
};

const ComposeFooter: React.FC<ComposeFooterProps> = ({
  selectedOption,
  onSelectOption,
  onSend,
  isSending,
  scheduledAt,
  onScheduledAtChange,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [menuOpen]);

  return (
    <div className={styles["compose-footer"]}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          flex: 1,
          minWidth: 0,
        }}
      >
        <div className={styles["send-options"]} ref={menuRef}>
          <button
            type="button"
            className={`${styles["compose-footer__dropdown"]}`}
            onClick={() => setMenuOpen((current) => !current)}
          >
            Send Options <span>▾</span>
          </button>
          {menuOpen && (
            <div className={styles["send-options__menu"]}>
              {SEND_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={[
                    styles["send-options__item"],
                    option === selectedOption
                      ? styles["send-options__item--active"]
                      : "",
                  ].join(" ")}
                  onClick={() => {
                    onSelectOption(option);
                    setMenuOpen(false);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedOption === "Schedule for later" && (
          <InputField
            label="Schedule date/time"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => onScheduledAtChange(e.target.value)}
          />
        )}
      </div>
      <button
        type="button"
        className={`${styles["compose-footer__btn"]} ${styles["compose-footer__btn--primary"]}`}
        onClick={onSend}
        disabled={isSending}
      >
        {isSending ? "Sending..." : selectedOption}
      </button>
    </div>
  );
};

const ComposeTab: React.FC = () => {
  const richEditorRef = useRef<RichTextEditorRef>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | number | null
  >(null);
  const [fromName, setFromName] = useState<string>("Kominiti Team");
  const [fromEmail, setFromEmail] = useState<string>("donotreply@kominiti.net");
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [richBody, setRichBody] = useState<string>("");
  const [bodyMode, setBodyMode] = useState<BodyMode>("Plain Text");
  const [sendOption, setSendOption] = useState<SendOption>("Send now");
  const [scheduledAt, setScheduledAt] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedAudience, setSelectedAudience] = useState<string>(AUDIENCE_OPTIONS[0].value);
  const [audienceConditions, setAudienceConditions] = useState<AudienceCondition[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Recipient[]>([]);

  const createCondition = (target_type: string): AudienceCondition => ({
    id: `${Date.now()}-${Math.random()}`,
    target_type,
    logic: "and",
  });

  const handleSelectAudience = (audience: string) => {
    setSelectedAudience(audience);
    if (audience === "all" || audience === "specific") {
      setAudienceConditions([]);
    }
    if (audience !== "specific") {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (user: Recipient) => {
    if (selectedUsers.some(u => u.id === user.id)) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleRemoveUser = (userId: number) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const addAudienceCondition = () => {
    const defaultConditionValue =
      AUDIENCE_OPTIONS[1]?.value || AUDIENCE_OPTIONS[0].value;
    setAudienceConditions((prev) => [
      ...prev,
      createCondition(defaultConditionValue),
    ]);
  };

  const updateAudienceCondition = (id: string, value: string) => {
    setAudienceConditions((prev) =>
      prev.map((condition) =>
        condition.id === id ? { ...condition, target_type: value } : condition,
      ),
    );
  };

  const removeAudienceCondition = (id: string) => {
    setAudienceConditions((prev) =>
      prev.filter((condition) => condition.id !== id),
    );
  };

  const getTemplates = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_HOST_API}mail-center/templates`,
      {
        headers: {
          Authorization: `Bearer ${isAuth()}`,
        },
      },
    );
    return response.data;
  };

  const { data: templatesData } = useQuery({
    queryKey: ["composeTemplates"],
    queryFn: getTemplates,
  });

  useEffect(() => {
    const loadedTemplates: Template[] = templatesData?.data?.data || [];
    setTemplates(loadedTemplates);
  }, [templatesData]);

  useEffect(() => {
    if (!selectedTemplateId || templates.length === 0) {
      return;
    }

    const selected = templates.find(
      (template) => template.id?.toString() === selectedTemplateId.toString(),
    );
    if (!selected) return;

    setSubject(selected.subject || "");
    const isRich = selected.body?.includes("<") && selected.body?.includes(">");
    if (isRich) {
      setBodyMode("Rich Text");
      setRichBody(selected.body || "");
      setBody("");
    } else {
      setBodyMode("Plain Text");
      setBody(selected.body || "");
      setRichBody("");
    }
  }, [selectedTemplateId, templates]);

  const formatScheduledAt = (value: string) => {
    if (!value) {
      return "";
    }

    const [date, time] = value.split("T");
    if (!date || !time) {
      return "";
    }

    const [hour, minute] = time.split(":");
    return `${date} ${hour}:${minute}:00`;
  };

  const handleInsertVariable = (variable: string) => {
    if (bodyMode === "Plain Text") {
      setBody((prev) => prev + variable);
      return;
    }
    richEditorRef.current?.insertVariable(variable);
  };

  const handleSendCampaign = async () => {
    setIsSubmitting(true);

    try {
      let finalBody = "";
      if (bodyMode === "Plain Text") finalBody = body;
      else if (bodyMode === "Rich Text") finalBody = richBody;
      else if (bodyMode === "Visual Editor") {
        try {
          if (richEditorRef.current?.exportHtml) {
            finalBody = await richEditorRef.current.exportHtml();
          } else {
            finalBody = richBody;
          }
        } catch (err) {
          console.error("Error exporting visual editor html:", err);
          finalBody = richBody;
        }
      }
      if (
        !subject.trim() ||
        !finalBody.trim() ||
        !fromName.trim() ||
        !fromEmail.trim()
      ) {
        swal(
          "Missing fields",
          "Please fill all required fields before sending.",
          "warning",
        );
        setIsSubmitting(false);
        return;
      }

      if (sendOption === "Schedule for later" && !scheduledAt) {
        swal("Missing schedule", "Please select a date and time for the scheduled send.", "warning");
        setIsSubmitting(false);
        return;
      }

      if (selectedAudience === "specific" && selectedUsers.length === 0) {
        swal("No users selected", "Please select at least one user for specific recipient type.", "warning");
        setIsSubmitting(false);
        return;
      }

      const status =
        sendOption === "Send now"
          ? "send"
          : sendOption === "Schedule for later"
            ? "scheduled"
            : "draft";

      const filteredConditions =
        selectedAudience === "all"
          ? []
          : [
              { target_type: selectedAudience },
              ...audienceConditions.map((condition) => ({
                target_type: condition.target_type,
                logic: condition.logic,
              })),
            ];

      const payload = {
        subject,
        body: finalBody,
        from_name: fromName,
        from_email: fromEmail,
        recipient_type: selectedAudience === "specific" ? "specific" : (filteredConditions.length > 0 ? "filtered" : "all"),
        ...(selectedAudience === "specific" ? { recipient_ids: selectedUsers.map(u => u.id) } : {}),
        ...(filteredConditions.length > 0 && selectedAudience !== "specific" ? { conditions: filteredConditions } : {}),
        ...(sendOption === "Schedule for later" ? { scheduled_at: formatScheduledAt(scheduledAt) } : {}),
        status,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_HOST_API}mail-center/campaigns`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
          },
        },
      );

      swal("Success!", response?.data?.message || "Campaign submitted successfully.", "success");
      // Reset form fields after successful submission
      setSubject("");
      setBody("");
      setRichBody("");
      richEditorRef.current?.reset();
      setFromName("Kominiti Team");
      setFromEmail("donotreply@kominiti.net");
      setSelectedTemplateId(null);
      setBodyMode("Plain Text");
      setSendOption("Send now");
      setScheduledAt("");
      setSelectedAudience(AUDIENCE_OPTIONS[0].value);
      setAudienceConditions([]);
      setSelectedUsers([]);
    } catch (error) {
      console.error("Error sending campaign:", error);
      swal("Error", "Unable to submit campaign. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles["compose-tab"]}>
      <div className={styles["compose-tab__body"]}>
        <div className={styles["compose-tab__left"]}>
          <TemplateSelector
            templates={templates}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={setSelectedTemplateId}
          />
          <EmailDetailsForm
            fromName={fromName}
            fromEmail={fromEmail}
            subject={subject}
            body={body}
            richBody={richBody}
            bodyMode={bodyMode}
            onFromNameChange={setFromName}
            onFromEmailChange={setFromEmail}
            onSubjectChange={setSubject}
            onBodyChange={setBody}
            onRichBodyChange={setRichBody}
            onBodyModeChange={setBodyMode}
            onInsertVariable={handleInsertVariable}
            editorRef={richEditorRef}
          />
          <ComposeFooter
            selectedOption={sendOption}
            onSelectOption={setSendOption}
            onSend={handleSendCampaign}
            isSending={isSubmitting}
            scheduledAt={scheduledAt}
            onScheduledAtChange={setScheduledAt}
          />
        </div>
        <div className={styles["compose-tab__right"]}>
          <TargetRecipientsPanel
            selectedAudience={selectedAudience}
            audienceConditions={audienceConditions}
            selectedUsers={selectedUsers}
            onSelectAudience={handleSelectAudience}
            onAddCondition={addAudienceCondition}
            onUpdateCondition={updateAudienceCondition}
            onRemoveCondition={removeAudienceCondition}
            onSelectUser={handleSelectUser}
            onRemoveUser={handleRemoveUser}
          />
        </div>
      </div>
    </div>
  );
};

export default ComposeTab;
